/**
 * voice-v8 — Werkzeugkarten: Medien: Morgenmeditation, Video-Analyse, Fokus-Videos.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { type Abspieler, baueTippHinweis } from "../abspielen.js";
import { absatz, aktionsKnopf, aktionsLeiste, baueKarte, fehlerBlock, hinweisKarte, kartenTitel, kuerzeListe, weitereHinweis } from "../karte.js";
import { buildCloseButton } from "../sheet-ui.js";
import { spielerAdresse } from "../../../shared/video-spieler.js";
import { unwrapResult } from "./hilfe.js";

/*
 * 26.09.2026 (Ticket 2556): die Morgenmeditation im Chat. Vespera verneinte
 * am 25.09. eine fertige Datei, weil kein Werkzeug sie kannte; jetzt fragt
 * sie `morgenmeditation`, und diese Karte bietet sie an.
 *
 * ⚠️ Der Player wird NICHT nachgebaut: „Anhören“ schickt dasselbe Ereignis
 *    wie Zuhause und Zeitleiste, und app.ts setzt den Player dieses Tages in
 *    `ort` (er spielt die nachts fertig vertonte Datei). Kein Selbststart —
 *    eine Karte aus dem Verlauf spielt nicht beim Neuladen los.
 */
export function buildMeditationCard(
  result: unknown,
  /**
   * Ticket 2698: `abspielen` holt sich hier, wie diese Karte startet — über
   * IHREN Knopf, ein Player-Weg. Gestartet wird nur live (dispatchToolCard),
   * nie beim Bau.
   */
  onAbspieler?: (a: Abspieler) => void,
): HTMLElement {
  const r = unwrapResult(result) as {
    ok?: boolean;
    error?: string;
    bereit?: boolean;
    /** Es gab sie, der Ton ist nach 14 Tagen weggeräumt (Ticket 2560). */
    tonWeg?: boolean;
    tag?: string;
    titel?: string;
    handlung?: string | null;
    minuten?: number | null;
  };
  if (r.ok === false) return hinweisKarte(r.error || "Die Morgenmeditation ist gerade nicht lesbar.");
  const tag = typeof r.tag === "string" && /^\d{4}-\d{2}-\d{2}$/.test(r.tag) ? r.tag : "";
  const tagName = r.titel || tag;
  // Nachbesserung zu pre.3352: nicht „keine“, wenn es sie gab und nur der Ton fehlt.
  if (r.tonWeg) {
    return hinweisKarte(`Für ${tagName || "diesen Tag"} ist der Ton der Morgenmeditation nicht mehr da — er wird nach 14 Tagen weggeräumt.`);
  }
  if (!r.bereit || !tag) {
    return hinweisKarte(`Für ${tagName || "diesen Tag"} gibt es keine fertige Morgenmeditation.`);
  }
  const el = baueKarte("morgenmeditation");
  el.appendChild(kartenTitel(`Morgenmeditation · ${tagName}`));
  if (r.handlung) {
        el.appendChild(absatz(r.handlung));
  }
  const ort = document.createElement("div");
  ort.className = "tool-meditation-spieler";
  const reihe = aktionsLeiste();
  const min = typeof r.minuten === "number" && r.minuten > 0 ? r.minuten : null;
  const knopf = aktionsKnopf({
    text: min ? `Anhören · ${min} ${min === 1 ? "Minute" : "Minuten"}` : "Anhören",
    primaer: true,
    symbol: "play",
    onClick: () => {
      document.dispatchEvent(new CustomEvent("vespera:meditation-anhoeren", { detail: { ort, tag } }));
      // Der Knopf ist jetzt weg — der Fokus geht auf den Player, nicht an den Seitenanfang.
      ort.querySelector<HTMLElement>("button")?.focus();
    },
  });
  reihe.appendChild(knopf);
  ort.appendChild(reihe);
  el.appendChild(ort);
  onAbspieler?.({
    // Nur eine Karte, die im Chat steht — sonst spielte ein unsichtbarer Player.
    spielen: () => {
      if (knopf.isConnected) knopf.click();
    },
    // Der Knopf steht schon da — kein zweiter daneben, nur der Grund.
    anbieten: () => {
      if (!knopf.isConnected) return;
      reihe.before(baueTippHinweis());
      knopf.focus();
    },
  });
  return el;
}

// analyze_video — strukturierte Video-Analyse statt rohem Markdown-Dump.
// Kopf mit YouTube-Thumbnail + Titel-Link + Meta, dann Zusammenfassung,
// Themen-Chips (reusen die .tool-ticket-badges/.tickets-badge der HD-/Ticket-
// Karte), Kernpunkte als .tool-search-results-Liste (gleicher Look), und bis zu
// 5 Zitate als Blockquotes. Transcript/Notes bewusst weggelassen (zu lang).
interface VideoAnalysisShape {
  title?: string;
  channel?: string;
  language?: string;
  duration_estimate?: string;
  summary?: string;
  topics?: string[];
  key_points?: string[];
  quotes?: Array<{ timestamp?: string; speaker?: string; text?: string }>;
  speakers?: string[];
}
interface VideoAnalyzeResultShape {
  ok?: boolean;
  url?: string;
  analysis?: VideoAnalysisShape;
  saved?: { id: string; title: string; project: string; path: string } | null;
  save_error?: string | null;
  error?: string;
}

// YouTube-Video-ID aus watch/youtu.be/shorts-URL ziehen (für das Thumbnail).
function extractYoutubeId(url: string): string | null {
  const m = /(?:watch\?v=|youtu\.be\/|shorts\/)([\w-]+)/.exec(url);
  return m ? m[1] : null;
}

// Liste optionaler Strings → getrimmte, nicht-leere Strings (typsicher).
function cleanStrings(arr?: Array<string | undefined>): string[] {
  if (!Array.isArray(arr)) return [];
  const out: string[] = [];
  for (const s of arr) {
    const t = typeof s === "string" ? s.trim() : "";
    if (t) out.push(t);
  }
  return out;
}

export function buildVideoAnalysisCard(result: unknown): HTMLElement {
  const r = unwrapResult(result) as VideoAnalyzeResultShape;
  const el = baueKarte("video");
  el.appendChild(kartenTitel("Video-Analyse"));

  const a = r.analysis;
  if (r.ok === false || !a) {
        el.appendChild(absatz(typeof r.error === "string" && r.error
        ? r.error
        : "Die Video-Analyse konnte nicht erstellt werden.", "warn"));
    return el;
  }

  const url = typeof r.url === "string" ? r.url : "";
  const title = a.title?.trim() || "Video";

  // --- Kopf: Thumbnail + Titel-Link + Meta-Zeile ---
  const head = document.createElement("div");
  head.className = "tool-video-head";

  const ytId = url ? extractYoutubeId(url) : null;
  if (ytId) {
    const thumb = document.createElement("img");
    thumb.className = "tool-video-thumb";
    thumb.src = `https://i.ytimg.com/vi/${ytId}/hqdefault.jpg`;
    thumb.alt = title;
    thumb.loading = "lazy";
    // Defekt-/Region-Fehler: Bild ausblenden statt kaputtes Icon zeigen.
    thumb.addEventListener("error", () => {
      thumb.hidden = true;
    });
    head.appendChild(thumb);
  }

  const headText = document.createElement("div");
  headText.className = "tool-video-headtext";
  if (url) {
    const link = document.createElement("a");
    link.className = "tool-video-title";
    link.href = url;
    link.target = "_blank";
    link.rel = "noopener";
    link.textContent = title;
    headText.appendChild(link);
  } else {
    const t = document.createElement("div");
    t.className = "tool-video-title";
    t.textContent = title;
    headText.appendChild(t);
  }
  const metaParts: string[] = [];
  if (a.channel?.trim()) metaParts.push(a.channel.trim());
  if (a.duration_estimate?.trim()) metaParts.push(a.duration_estimate.trim());
  if (a.language?.trim()) metaParts.push(a.language.trim());
  if (metaParts.length > 0) {
    const meta = document.createElement("div");
    meta.className = "tool-video-meta";
    meta.textContent = metaParts.join(" · ");
    headText.appendChild(meta);
  }
  head.appendChild(headText);
  el.appendChild(head);

  // --- Zusammenfassung ---
  if (a.summary?.trim()) {
    const sum = document.createElement("p");
    sum.className = "tool-video-summary";
    sum.textContent = a.summary.trim();
    el.appendChild(sum);
  }

  // --- Themen als Chips (reuse Ticket-/HD-Badge-Look) ---
  const topics = cleanStrings(a.topics);
  if (topics.length > 0) {
    const chips = document.createElement("div");
    chips.className = "tool-ticket-badges";
    for (const t of topics) {
      const chip = document.createElement("span");
      chip.className = "tickets-badge tickets-badge-low";
      chip.textContent = t;
      chips.appendChild(chip);
    }
    el.appendChild(chips);
  }

  // --- Kernpunkte (gleiche Liste wie web_search) ---
  const points = cleanStrings(a.key_points);
  if (points.length > 0) {
    const ul = document.createElement("ul");
    ul.className = "tool-video-points";
    for (const p of points) {
      const li = document.createElement("li");
      li.textContent = p;
      ul.appendChild(li);
    }
    el.appendChild(ul);
  }

  // --- Zitate als Blockquotes (max 5) ---
  const quotes = (a.quotes ?? []).filter((q) => q?.text?.trim());
  for (const q of quotes.slice(0, 5)) {
    const bq = document.createElement("blockquote");
    bq.className = "tool-video-quote";
    const cite = [q.speaker?.trim(), q.timestamp?.trim()]
      .filter(Boolean)
      .join(" · ");
    if (cite) {
      const c = document.createElement("span");
      c.className = "tool-video-quote-cite";
      c.textContent = cite;
      bq.appendChild(c);
    }
    const txt = document.createElement("span");
    txt.className = "tool-video-quote-text";
    txt.textContent = `„${(q.text ?? "").trim()}"`;
    bq.appendChild(txt);
    el.appendChild(bq);
  }

  // --- Fuß: YouTube-Link + Speicher-Status ---
  if (url) {
    aktionsLeiste(el).appendChild(aktionsKnopf({ text: "Auf YouTube ansehen", primaer: true, href: url, neuesFenster: true }));
  }
  if (r.saved?.project) {
    const note = document.createElement("div");
    note.className = "tool-video-saved";
    note.textContent = `Im Projekt „${r.saved.project}" gespeichert`;
    el.appendChild(note);
  } else if (typeof r.save_error === "string" && r.save_error) {
        el.appendChild(absatz(r.save_error, "warn"));
  }

  return el;
}

/**
 * Fokus-Videos: drei geprüfte Treffer, abgespielt IN Vespera.
 *
 * Martin: *„…ohne dass ich in die Verleitung komme, wieder Shorts
 * anzuschauen oder andere Videos, sodass ich den Fokus auf das Thema
 * halten kann, ohne in eine neue Sucht zu fallen."*
 *
 * ## Was diese Karte anders macht als die zwei Video-Karten daneben
 *
 * `buildVideoAnalysisCard` (oben) macht ihren Titel zu einem
 * `target="_blank"`-Link nach youtube.com. Die Musik-Blase in `app.ts`
 * hängt unter JEDEN Spieler „In YouTube öffnen" — auf eine `list=RD…`-
 * Adresse, also einen Mix ohne Ende. Auf Martins iPhone öffnet das nicht
 * eine Webseite, sondern die YouTube-APP: die volle Oberfläche inklusive
 * Shorts. Das ist genau das Versagen, das er beschreibt — und es steht
 * heute schon in diesem Repo.
 *
 * Hier gibt es KEINEN solchen Weg. Kein `target="_blank"`, keine
 * watch-Adresse, kein „In YouTube öffnen". Wer das später hinzufügt,
 * nimmt dem Modul seinen einzigen Zweck; `tests/frontend/fokus-videos.test.ts`
 * hält dagegen.
 *
 * ## Was diese Karte NICHT kann — und was Martin wissen muss
 *
 * ⚠️ Drei Türen bleiben offen, und sie lassen sich nicht schließen:
 *
 *   1. Der TITEL und der KANAL-AVATAR im Spieler sind immer sichtbar und
 *      immer Links. Seit YouTube am 25.09.2018 `showinfo` abgeschafft hat,
 *      gibt es keinen Parameter mehr dagegen.
 *   2. Die END SCREENS des Uploaders in den letzten bis zu zwanzig
 *      Sekunden — anklickbare Kacheln, vom Kanalinhaber gesetzt.
 *      `iv_load_policy=3` hilft NICHT: das betrifft die alten Annotations,
 *      die es seit Januar 2019 nicht mehr gibt.
 *   3. WERBUNG im eingebetteten Spieler.
 *
 * Diese Türen zu verriegeln wäre technisch einfach (`sandbox` ohne
 * `allow-popups`, ein Overlay, `pointer-events: none`) und ist von
 * YouTube ausdrücklich untersagt: *„You must not remove, obscure, alter,
 * or disable any links that appear in YouTube players"* und *„must not
 * display overlays, frames, or other visual elements in front of any part
 * of a YouTube embedded player"*. Auch der Umweg über einen eigenen
 * Zwischen-Rahmen ist benannt und verboten.
 *
 * Deshalb steht unter der Auswahl EIN ehrlicher Satz statt eines
 * Versprechens. Eine Mauer, die nur eine Markierung am Boden ist, kostet
 * beim ersten Durchgehen nicht nur den Fokus, sondern das Vertrauen.
 */
export function buildFokusVideosCard(result: unknown): HTMLElement {
  const r = unwrapResult(result);
  const el = baueKarte("fokus-videos");
  const thema = typeof r.thema === "string" ? r.thema : "";
  el.appendChild(kartenTitel(thema ? `Videos zu „${thema}“` : "Fokus-Videos"));

  if (r.ok !== true || !Array.isArray(r.treffer) || r.treffer.length === 0) {
    el.appendChild(
      fehlerBlock(
        typeof r.error === "string"
          ? r.error
          : "Nichts Passendes gefunden — lieber nichts als Füllmaterial.",
      ),
    );
    return el;
  }

  const videos = r.treffer as FokusVideoDaten[];
  const liste = document.createElement("div");
  liste.className = "fokus-liste";
  // 15.10.2026: höchstens LISTE_MAX Zeilen — eine Auswahl, keine Mediathek (Karten-Größe).
  const gekuerzt = kuerzeListe(videos);
  gekuerzt.sichtbar.forEach((v, i) => liste.appendChild(buildFokusZeile(v, videos, i)));
  el.appendChild(liste);
  weitereHinweis(el, gekuerzt.rest);

  // Was die Auswahl gekostet hat — Martin soll sehen, dass gefiltert wurde,
  // nicht nur behauptet.
  const fuss = document.createElement("div");
  fuss.className = "fokus-fuss";
  const teile: string[] = [];
  if (typeof r.geprueft === "number") teile.push(`${r.geprueft} geprüft`);
  if (typeof r.shortsVerworfen === "number" && r.shortsVerworfen > 0) {
    teile.push(
      r.shortsVerworfen === 1
        ? "1 Short verworfen"
        : `${r.shortsVerworfen} Shorts verworfen`,
    );
  }
  teile.push("Wiedergabe in Vespera");
  fuss.textContent = teile.join(" · ");
  el.appendChild(fuss);
  return el;
}

interface FokusVideoDaten {
  videoId: string;
  titel: string;
  kanal: string;
  dauerText: string;
  aufrufe: number | null;
  embedUrl: string;
  thumbnail: string;
}

/** Eine Zeile der Auswahl — Bild, Titel, gemessene Fakten. */
function buildFokusZeile(
  v: FokusVideoDaten,
  alle: FokusVideoDaten[],
  index: number,
): HTMLElement {
  /*
   * ⚠️ Ein <button>, KEIN <a>. Ein Anker mit einer YouTube-Adresse wäre
   * über das Kontextmenü („Link kopieren", „In neuem Tab öffnen") ein
   * Ausgang — auch ohne target="_blank".
   */
  const zeile = document.createElement("button");
  zeile.type = "button";
  zeile.className = "fokus-zeile";

  const bild = document.createElement("img");
  bild.className = "fokus-bild";
  bild.src = v.thumbnail;
  bild.alt = "";
  bild.loading = "lazy";
  zeile.appendChild(bild);

  const text = document.createElement("span");
  text.className = "fokus-text";
  const titel = document.createElement("span");
  titel.className = "fokus-titel";
  titel.textContent = v.titel;
  const meta = document.createElement("span");
  meta.className = "fokus-meta";
  // Die gemessenen Fakten SIND die Begründung. Erfundene Prosa („dieses,
  // weil es besonders anschaulich ist") wäre eine Behauptung.
  const m: string[] = [v.dauerText];
  if (typeof v.aufrufe === "number") m.push(`${aufrufeKurz(v.aufrufe)} Aufrufe`);
  if (v.kanal) m.push(v.kanal);
  meta.textContent = m.join(" · ");
  text.append(titel, meta);
  zeile.appendChild(text);

  zeile.addEventListener("click", () => openFokusPlayer(alle, index));
  return zeile;
}

/** 1.234.567 → „1,2 Mio“. Eine genaue Zahl liest hier niemand. */
function aufrufeKurz(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(".", ",")} Mio`;
  if (n >= 1000) return `${Math.round(n / 1000)} Tsd.`;
  return String(n);
}

/**
 * Der Spieler — in Vespera, mit Vesperas eigener Weiterleitung darunter.
 *
 * ⚠️ „darunter", nicht „darüber": ein Element VOR dem Spieler ist ein
 * verbotenes Overlay. Es liegt unter ihm und ist trotzdem der auffälligere
 * Weg, weil es der einzige ist, der zum nächsten geprüften Video führt.
 */
function openFokusPlayer(videos: FokusVideoDaten[], start: number): void {
  document.getElementById("fokus-player-modal")?.remove();
  let index = start;

  const overlay = document.createElement("div");
  overlay.id = "fokus-player-modal";
  overlay.className = "modal-overlay";
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) overlay.remove();
  });

  const panel = document.createElement("div");
  panel.className = "modal-panel modal-panel--fokus";

  const closeBtn = buildCloseButton(() => overlay.remove());
  closeBtn.classList.add("close-btn--corner");
  panel.appendChild(closeBtn);

  const heading = document.createElement("h2");
  heading.className = "modal-title";
  panel.appendChild(heading);

  const frame = document.createElement("iframe");
  frame.className = "fokus-frame";
  /*
   * ⚠️ KEIN sandbox-Attribut. Es würde Titel- und Endraster-Klicks ins
   * Leere laufen lassen und wäre damit genau die Dichtheit, die Martin
   * will — und es ist ausdrücklich untersagt („disable any links that
   * appear in YouTube players"). Wer es hier einbaut, riskiert den
   * Zugang für die ganze App.
   */
  /*
   * `allowfullscreen` UND `fullscreen` in `allow` — die Berechtigungs-
   * Richtlinie ist die neuere Form, das alte Attribut brauchen ältere
   * WebKit-Fassungen noch. Ohne beides bleibt der Vollbild-Knopf im
   * Spieler wirkungslos, auch wenn `fs=1` gesetzt ist.
   */
  frame.setAttribute("allowfullscreen", "");
  frame.setAttribute(
    "allow",
    "accelerometer; encrypted-media; fullscreen; gyroscope; picture-in-picture",
  );
  frame.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
  panel.appendChild(frame);

  const weiter = document.createElement("div");
  weiter.className = "fokus-weiter";
  panel.appendChild(weiter);

  const hinweis = document.createElement("p");
  hinweis.className = "fokus-hinweis";
  /*
   * Der ehrliche Satz. Er steht hier, weil Martin sich sonst auf eine
   * Abschirmung verlässt, die es so nicht gibt — und beim ersten Klick
   * auf den Titel im Spieler in der YouTube-App landet.
   */
  hinweis.textContent =
    "Vespera spielt hier ab, ohne deinen YouTube-Verlauf zu füttern. " +
    "Titel, Kanal und die Kacheln am Videoende gehören YouTube — " +
    "ein Tipp darauf führt hinaus.";
  panel.appendChild(hinweis);

  function zeige(i: number): void {
    index = i;
    const v = videos[i];
    heading.textContent = v.titel;
    /*
     * ⚠️ NICHT `v.embedUrl` — die steht als Text in der gespeicherten
     * Chat-Nachricht und ist so alt wie die Nachricht. Martins Karte vom
     * Vormittag trug nach dem Neustart immer noch `fs=0`, also kein
     * Vollbild, obwohl der Server längst `fs=1` lieferte. Frisch gebaut
     * bekommt auch eine Karte von gestern die heutigen Parameter.
     */
    frame.src = spielerAdresse(v.embedUrl || v.videoId) ?? "";
    frame.setAttribute("title", v.titel);
    weiter.textContent = "";

    const naechstes = videos[i + 1];
    if (naechstes) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "modal-btn";
      b.textContent = "Nächstes zum Thema";
      b.addEventListener("click", () => zeige(i + 1));
      weiter.appendChild(b);
    }
    const fertig = document.createElement("button");
    fertig.type = "button";
    fertig.className = "modal-btn modal-btn--secondary";
    fertig.textContent = naechstes ? "Reicht mir" : "Zurück zum Chat";
    fertig.addEventListener("click", () => overlay.remove());
    weiter.appendChild(fertig);

    const zaehler = document.createElement("span");
    zaehler.className = "fokus-zaehler";
    zaehler.textContent = `${i + 1} von ${videos.length}`;
    weiter.appendChild(zaehler);
  }
  zeige(index);

  overlay.appendChild(panel);
  document.body.appendChild(overlay);
}
